/**
 * Ported VERBATIM from apr_template `src/pages/Dashboard.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect } from 'react';
import { authFetch } from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor } from '../dispositionPalette';
import type { Help } from '../compat/helpTypes';
// Decision Workspace design (owner ruling 2026-10-21): kit icons and token pairs; disposition slices stay on the matrix.
import { Icon, icons } from '@platform/ui-kit/workspace';
import { WS_CARD, WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType, type WsTone } from '../workspace/ws';

const DashboardHelp: Help = {
  title: 'Executive Dashboard',
  icon: '📊',
  purpose: 'A portfolio-wide overview of your APR program — key metrics, disposition mix, assessment progress, benefit realization, and integration-group status, all scoped to a chosen integration grouping.',
  doHere: [
    'Pick an <b>integration grouping</b> from the dropdown (or "All Groupings") to load the dashboard.',
    'Read the top metric cards: total applications, APR models, integration groups, deployed apps, and expected 2-year savings.',
    'Review the <b>Portfolio by Disposition</b> donut and <b>Overall Progress</b> rings for assessed vs. deployed share.',
    'Scan the <b>APR Models Lifecycle</b> table for QA, disposition, development, and deployment progress per model.',
    'Use <b>Refresh</b> to reload the latest figures.',
  ],
  fits: 'Executive Dashboard',
  firstTime: 'Select an integration grouping from the dropdown to populate every panel.',
  sections: [
    {
      type: 'defs',
      label: 'Disposition colors',
      items: [
        { term: 'Retire', color: '#dc3545', def: 'Score 1.00–2.00 — decommission the application.' },
        { term: 'Re-Platform', color: '#fd7e14', def: 'Score 2.01–3.00 — move to a better platform with minimal change.' },
        { term: 'Re-Design', color: '#ffc107', def: 'Score 3.01–4.00 — significantly rework the application.' },
        { term: 'Retain', color: '#28a745', def: 'Score 4.01–5.00 — keep the application as-is.' },
      ],
    },
  ],
  links: [
    { href: '/customers/applications', label: 'Application Master Data' },
    { href: '/ai-analysis/results', label: 'Analysis Results' },
  ],
};

interface IntegrationGroup {
  group_id: string;
  group_name: string;
}

interface ExecutiveDashboardData {
  keyMetrics: {
    totalApplications: number;
    totalModels: number;
    totalGroups: number;
    totalRunRate: number;
    completedAssessments: number;
    deployedApps: number;
    expectedSavings2Yr: number;
  };
  portfolioByDisposition: Array<{
    disposition: string;
    app_count: string;
  }>;
  aprModelsSummary: Array<{
    id: string;
    model_name: string;
    status: string;
    created_at: string;
    app_count: string;
    qa_completed: string;
    disposition_completed: string;
    in_development: string;
    deployed: string;
  }>;
  integrationGroupsSummary: Array<{
    group_id: string;
    group_name: string;
    status: string;
    app_count: string;
    total_run_rate: string;
    deployed_count: string;
    in_dev_count: string;
  }>;
  benefitSummary: Array<{
    grouping_id: string;
    group_name: string;
    app_count: string;
    total_tco: string;
    total_frc: string;
    total_dc: string;
    total_2yr_savings: string;
    roi_percentage: string;
  }>;
  assessmentByDimension: Array<{
    dimension_name: string;
    total_apps: string;
    completed_apps: string;
    avg_score: string;
  }>;
  topApplications: Array<{
    id: string;
    application_name: string;
    application_id_code: string;
    recommendation: string;
    annual_run_rate: string;
    assessment_status: string;
  }>;
}

// DW: chart text is 12px muted; series colours are kit tokens except disposition slices (matrix).
const CHART_TEXT: React.CSSProperties = { ...wsType('meta'), color: WS_COLOR.textMuted };
const CARD_TITLE: React.CSSProperties = { margin: '0 0 20px', ...wsType('sectionTitle'), color: WS_COLOR.text };
const PANEL: React.CSSProperties = { ...WS_CARD, padding: '24px' };
const STAT_VALUE: React.CSSProperties = { ...wsType('pageTitle'), color: WS_COLOR.text };
const STAT_LABEL: React.CSSProperties = { ...wsType('label'), color: WS_COLOR.textMuted, marginTop: '4px' };
const STAT_TILE: React.CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: WS_RADIUS.card,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
// DW: sticky goes on the th (not the tr); the table uses separate borders so it can stick.
const STICKY_TH: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 1 };
const TABLE: React.CSSProperties = { width: '100%', borderCollapse: 'separate', borderSpacing: 0 };
const BAR_TRACK: React.CSSProperties = { width: '60px', height: '8px', backgroundColor: WS_COLOR.surfaceSubtle, borderRadius: '4px', overflow: 'hidden' };
const PCT_TEXT: React.CSSProperties = { ...CHART_TEXT, minWidth: '40px' };

// Donut Chart Component
const DonutChart: React.FC<{
  data: Array<{ label: string; value: number; color: string }>;
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
  centerValue?: string | number;
}> = ({ data, size = 160, strokeWidth = 24, centerLabel, centerValue }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  let currentOffset = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={WS_COLOR.surfaceSubtle}
          strokeWidth={strokeWidth}
        />
        {/* Data segments */}
        {data.map((item, index) => {
          const percentage = total > 0 ? item.value / total : 0;
          const strokeDasharray = `${percentage * circumference} ${circumference}`;
          const strokeDashoffset = -currentOffset * circumference;
          currentOffset += percentage;

          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'all 0.5s ease' }}
            />
          );
        })}
      </svg>
      {/* Center text */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center'
      }}>
        <div style={{ ...wsType('pageTitle'), color: WS_COLOR.text }}>{centerValue}</div>
        <div style={CHART_TEXT}>{centerLabel}</div>
      </div>
    </div>
  );
};

// Progress Ring Component
const ProgressRing: React.FC<{
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  label?: string;
}> = ({ percentage, size = 60, strokeWidth = 6, color = WS_COLOR.primary, label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={WS_COLOR.surfaceSubtle}
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          ...wsType('meta'),
          fontWeight: 600,
          color: WS_COLOR.text
        }}>
          {percentage}%
        </div>
      </div>
      {label && <div style={{ ...CHART_TEXT, textAlign: 'center' }}>{label}</div>}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [data, setData] = useState<ExecutiveDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load integration groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await authFetch('/api/integration-groups?exclude_analysis=true');
        if (response.ok) {
          const result = await response.json();
          const groups = Array.isArray(result) ? result : (result.data || result.groups || []);
          setIntegrationGroups(groups);
        }
      } catch (err) {
        console.error('Error loading integration groups:', err);
      }
    };
    fetchGroups();
  }, []);

  // Load dashboard data when group changes
  useEffect(() => {
    if (!selectedGroupId) {
      setData(null);
      return;
    }
    loadDashboardData();
  }, [selectedGroupId]);

  const loadDashboardData = async () => {
    if (!selectedGroupId) return;

    setLoading(true);
    setError(null);
    try {
      let url = '/api/dashboard/executive';
      if (selectedGroupId !== 'ALL') {
        url += `?integration_group_id=${encodeURIComponent(selectedGroupId)}`;
      }

      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to load executive dashboard data');
      }
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num || 0);
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseInt(num) : num;
    return new Intl.NumberFormat('en-US').format(n || 0);
  };

  // PORT NOTE: Disposition Matrix colours (§1.9.3 rule 3), not a hard-coded palette.
  const getDispositionColor = (disposition: string) => matrixColor(disposition).base;

  const getStatusBadge = (status: string) => {
    // DW: each status keeps its hue family as a measured kit pair.
    const tones: Record<string, WsTone> = {
      'Created': 'info',
      'Scheduled': 'warning',
      'In-Process': 'info',
      'Completed': 'success',
      'Deployed': 'success',
      'In Development': 'info'
    };
    const tone = tones[status] || 'neutral';
    return (
      <span style={{ ...WS_CHIP, ...wsTone(tone) }}>
        {status}
      </span>
    );
  };

  const groupSelect = (
    <select
      value={selectedGroupId}
      onChange={(e) => setSelectedGroupId(e.target.value)}
      style={{ minWidth: '280px' }}
    >
      <option value="">Select Grouping ID</option>
      <option value="ALL">All Groupings</option>
      {integrationGroups.map(group => (
        <option key={group.group_id} value={group.group_id}>
          {group.group_id} - {group.group_name}
        </option>
      ))}
    </select>
  );

  // Empty state
  if (!selectedGroupId) {
    return (
      <div style={{ minHeight: '100vh', padding: '24px' }}>
        <HelpPanel help={DashboardHelp} />
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <PageHeader
            title="Executive Dashboard"
            subtitle="Application Portfolio Rationalization Overview"
            actions={<>{groupSelect}</>}
          />

          {/* Empty State Card */}
          <div style={{ ...WS_CARD, padding: '80px 40px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              ...wsTone('info'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Icon icon={icons.BarChart3} size={24} />
            </div>
            <h2 style={{ margin: '0 0 12px', ...wsType('pageTitle'), color: WS_COLOR.text }}>
              Select an Integration Grouping
            </h2>
            <p style={{ ...wsType('body'), color: WS_COLOR.textMuted, margin: 0, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              Choose a grouping from the dropdown above to view comprehensive portfolio analytics and insights.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', padding: '24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ ...WS_CARD, padding: '80px 40px', textAlign: 'center' }}>
            <div style={{ marginBottom: '16px', color: WS_COLOR.textMuted }}>
              <Icon icon={icons.Loader2} size={24} />
            </div>
            <p style={{ ...wsType('body'), color: WS_COLOR.textMuted, margin: 0 }}>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', padding: '24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ ...WS_CARD, padding: '40px', textAlign: 'center' }}>
            <p style={{ ...wsType('body'), color: WS_STATUS.error.ink, marginBottom: '16px' }}>{error}</p>
            <button className="btn btn-primary" onClick={loadDashboardData}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate totals for disposition chart
  const dispositionData = data.portfolioByDisposition.map(item => ({
    label: item.disposition,
    value: parseInt(item.app_count) || 0,
    color: getDispositionColor(item.disposition)
  }));
  const totalAppsInDisposition = dispositionData.reduce((sum, item) => sum + item.value, 0);

  // Calculate overall progress
  const totalApps = data.keyMetrics.totalApplications;
  const assessedPct = totalApps > 0 ? Math.round((data.keyMetrics.completedAssessments / totalApps) * 100) : 0;
  const deployedPct = totalApps > 0 ? Math.round((data.keyMetrics.deployedApps / totalApps) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '40px' }}>
      <HelpPanel help={DashboardHelp} />
      <PageHeader
        title="Executive Dashboard"
        subtitle="Application Portfolio Rationalization Overview"
        actions={<>
          {groupSelect}
          <button className="btn" onClick={loadDashboardData}>
            Refresh
          </button>
        </>}
      />

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        {/* Key Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Applications */}
          <div style={PANEL}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={STAT_VALUE}>
                  {formatNumber(data.keyMetrics.totalApplications)}
                </div>
                <div style={STAT_LABEL}>Total Applications</div>
              </div>
              <div style={{ ...STAT_TILE, ...wsTone('info') }}>
                <Icon icon={icons.LayoutGrid} size={24} />
              </div>
            </div>
          </div>

          {/* APR Models */}
          <div style={PANEL}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={STAT_VALUE}>
                  {formatNumber(data.keyMetrics.totalModels)}
                </div>
                <div style={STAT_LABEL}>APR Models</div>
              </div>
              <div style={{ ...STAT_TILE, ...wsTone('info') }}>
                <Icon icon={icons.Target} size={24} />
              </div>
            </div>
          </div>

          {/* Integration Groups */}
          <div style={PANEL}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={STAT_VALUE}>
                  {formatNumber(data.keyMetrics.totalGroups)}
                </div>
                <div style={STAT_LABEL}>Integration Groups</div>
              </div>
              <div style={{ ...STAT_TILE, ...wsTone('info') }}>
                <Icon icon={icons.Link2} size={24} />
              </div>
            </div>
          </div>

          {/* Deployed */}
          <div style={PANEL}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={STAT_VALUE}>
                  {formatNumber(data.keyMetrics.deployedApps)}
                </div>
                <div style={STAT_LABEL}>Deployed</div>
              </div>
              <div style={{ ...STAT_TILE, ...wsTone('success') }}>
                <Icon icon={icons.CheckCircle2} size={24} />
              </div>
            </div>
          </div>

          {/* Expected Savings */}
          {/* DW: the highlighted card keeps its emphasis as the success pair (fill + ink together). */}
          <div style={{ ...PANEL, background: WS_STATUS.success.fill, borderColor: WS_STATUS.success.fill }}>
            <div>
              <div style={{ ...STAT_VALUE, color: WS_STATUS.success.ink }}>
                {formatCurrency(data.keyMetrics.expectedSavings2Yr)}
              </div>
              <div style={{ ...STAT_LABEL, color: WS_STATUS.success.ink }}>Expected 2-Year Savings</div>
            </div>
          </div>
        </div>

        {/* Second Row - Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          {/* Disposition Breakdown */}
          <div style={PANEL}>
            <h3 style={CARD_TITLE}>
              Portfolio by Disposition
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
              <DonutChart
                data={dispositionData}
                size={140}
                strokeWidth={20}
                centerValue={totalAppsInDisposition}
                centerLabel="Apps"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {dispositionData.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      backgroundColor: item.color
                    }} />
                    <span style={{ ...wsType('label'), fontWeight: 400, color: WS_COLOR.text }}>{item.label}</span>
                    <span style={{ ...wsType('label'), fontWeight: 600, color: WS_COLOR.text, marginLeft: 'auto' }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Overall Progress */}
          <div style={PANEL}>
            <h3 style={CARD_TITLE}>
              Overall Progress
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <ProgressRing percentage={assessedPct} size={80} strokeWidth={8} color={WS_COLOR.primary} label="Assessed" />
              <ProgressRing percentage={deployedPct} size={80} strokeWidth={8} color={WS_STATUS.success.ink} label="Deployed" />
            </div>
          </div>

          {/* Quick Stats */}
          <div style={PANEL}>
            <h3 style={CARD_TITLE}>
              Assessment Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...wsType('label'), color: WS_COLOR.textMuted }}>Completed Assessments</span>
                <span style={{ ...wsType('sectionTitle'), color: WS_COLOR.primary }}>
                  {formatNumber(data.keyMetrics.completedAssessments)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...wsType('label'), color: WS_COLOR.textMuted }}>Applications Deployed</span>
                <span style={{ ...wsType('sectionTitle'), color: WS_STATUS.success.ink }}>
                  {formatNumber(data.keyMetrics.deployedApps)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ ...wsType('label'), color: WS_COLOR.textMuted }}>Pending Assessment</span>
                <span style={{ ...wsType('sectionTitle'), color: WS_STATUS.warning.ink }}>
                  {formatNumber(totalApps - data.keyMetrics.completedAssessments)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* APR Models Progress */}
        <div style={{ ...PANEL, marginBottom: '24px' }}>
          <h3 style={CARD_TITLE}>
            APR Models Lifecycle
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={TABLE}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Model Name</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Apps</th>
                  <th style={{ textAlign: 'center' }}>QA Assessment</th>
                  <th style={{ textAlign: 'center' }}>Disposition</th>
                  <th style={{ textAlign: 'center' }}>In Development</th>
                  <th style={{ textAlign: 'center' }}>Deployed</th>
                </tr>
              </thead>
              <tbody>
                {data.aprModelsSummary.map((model, idx) => {
                  const total = parseInt(model.app_count) || 0;
                  const qaCompleted = parseInt(model.qa_completed) || 0;
                  const dispCompleted = parseInt(model.disposition_completed) || 0;
                  const inDev = parseInt(model.in_development) || 0;
                  const deployed = parseInt(model.deployed) || 0;
                  const qaPct = total > 0 ? Math.round((qaCompleted / total) * 100) : 0;
                  const dispPct = total > 0 ? Math.round((dispCompleted / total) * 100) : 0;
                  const inDevPct = total > 0 ? Math.round((inDev / total) * 100) : 0;
                  const deployedPct = total > 0 ? Math.round((deployed / total) * 100) : 0;

                  return (
                    <tr key={idx}>
                      <td style={{ fontWeight: 500 }}>{model.model_name}</td>
                      <td style={{ textAlign: 'center' }}>{getStatusBadge(model.status)}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{total}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={BAR_TRACK}>
                            <div style={{ width: `${qaPct}%`, height: '100%', backgroundColor: WS_COLOR.primary, borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={PCT_TEXT}>{qaPct}%</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={BAR_TRACK}>
                            <div style={{ width: `${dispPct}%`, height: '100%', backgroundColor: WS_STATUS.info.ink, borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={PCT_TEXT}>{dispPct}%</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={BAR_TRACK}>
                            <div style={{ width: `${inDevPct}%`, height: '100%', backgroundColor: WS_STATUS.warning.ink, borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={PCT_TEXT}>{inDevPct}%</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={BAR_TRACK}>
                            <div style={{ width: `${deployedPct}%`, height: '100%', backgroundColor: WS_STATUS.success.ink, borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={PCT_TEXT}>{deployedPct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Row - Benefit Realization & Groups */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Benefit Realization */}
          <div style={PANEL}>
            <h3 style={CARD_TITLE}>
              Benefit Realization by Group
            </h3>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              <table style={TABLE}>
                <thead>
                  <tr>
                    <th style={{ ...STICKY_TH, textAlign: 'left' }}>Grouping ID</th>
                    <th style={{ ...STICKY_TH, textAlign: 'left' }}>Group Name</th>
                    <th style={{ ...STICKY_TH, textAlign: 'right' }}>2-Yr Savings</th>
                    <th style={{ ...STICKY_TH, textAlign: 'right' }}>ROI %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.benefitSummary.map((item, idx) => {
                    const roiValue = parseFloat(item.roi_percentage) || 0;
                    const savingsValue = parseFloat(item.total_2yr_savings) || 0;
                    return (
                      <tr key={idx}>
                        <td style={{ ...wsType('mono'), color: WS_COLOR.text }}>{item.grouping_id || '-'}</td>
                        <td style={{ color: WS_COLOR.textMuted }}>{item.group_name || 'Ungrouped'}</td>
                        <td style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          color: savingsValue >= 0 ? WS_STATUS.success.ink : WS_STATUS.error.ink
                        }}>
                          {formatCurrency(item.total_2yr_savings)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{ ...WS_CHIP, ...wsTone(roiValue >= 0 ? 'success' : 'error') }}>
                            {roiValue.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Integration Groups Progress */}
          <div style={PANEL}>
            <h3 style={CARD_TITLE}>
              Integration Groups Progress
            </h3>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              <table style={TABLE}>
                <thead>
                  <tr>
                    <th style={{ ...STICKY_TH, textAlign: 'left' }}>Group</th>
                    <th style={{ ...STICKY_TH, textAlign: 'center' }}>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {data.integrationGroupsSummary.map((group, idx) => {
                    const total = parseInt(group.app_count) || 0;
                    const deployed = parseInt(group.deployed_count) || 0;
                    const progress = total > 0 ? Math.round((deployed / total) * 100) : 0;
                    return (
                      <tr key={idx}>
                        <td>
                          <div style={{ fontWeight: 500, color: WS_COLOR.text }}>{group.group_name}</div>
                          <div style={{ ...wsType('mono'), color: WS_COLOR.textMuted }}>{group.group_id}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ ...BAR_TRACK, width: undefined, flex: 1 }}>
                              <div style={{
                                width: `${progress}%`,
                                height: '100%',
                                backgroundColor: progress === 100 ? WS_STATUS.success.ink : WS_COLOR.primary,
                                borderRadius: '4px',
                                transition: 'width 0.5s'
                              }} />
                            </div>
                            <span style={{ ...PCT_TEXT, fontWeight: 600 }}>
                              {progress}%
                            </span>
                          </div>
                          <div style={{ ...CHART_TEXT, marginTop: '4px' }}>
                            {deployed}/{total} apps deployed
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
